import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { ColDef } from '@ag-grid-community/core';
import { DialogService } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { Account, JoHeader } from '@/model/entities/entity-model';
import { DbQueryService } from '@/shared/db-query.service';
import { DateFns } from '@/shared/date-fns';
import { UtilFns } from '@/shared/util-fns';
import { environment } from 'environments/environment';
import _ from 'lodash';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  SelectShippingAddressDialogComponent,
  ShippingAddressItem,
} from 'app/job-orders/select-shipping-address-dialog.component';

interface JoInProcessData {
  id: number;
  accountName: string;
  joDate: Date;
  joStatusName: string;
  openValueAmt: number;
}

@Component({
  selector: 'app-jo-in-process-widget',
  template: `
    <div *ngIf="isReady" class="h-full flex flex-col items-center justify-center p-4">
      <div class="text-5xl font-bold text-primary mb-2">{{ count }}</div>
      <div class="text-gray-600 text-sm">Job Orders In Process</div>
      <div class="text-2xl font-semibold mt-3">{{ totalOpenValueAmt | currency: 'USD' : 'symbol' : '1.0-0' }}</div>
      <div class="text-gray-600 text-sm">Open Value</div>
      <div *ngIf="oldestDate" class="text-gray-400 text-xs mt-2 mb-2">Oldest: {{ oldestDate }}</div>
      <div *ngIf="!oldestDate" class="mb-4"></div>
      <button
        pButton
        label="View All"
        class="p-button-sm p-button-outlined"
        (click)="navigateToInProcess()">
      </button>
      <button
        *ngIf="account"
        pButton
        label="Print Worksheet"
        class="p-button-sm p-button-outlined mt-2"
        icon="pi pi-print"
        (click)="printWorksheet()">
      </button>
    </div>
    <div *ngIf="!isReady" class="flex items-center justify-center h-full">
      <i class="pi pi-spin pi-spinner text-2xl"></i>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: calc(100% - 40px);
    }
  `],
  standalone: false,
})
export class JoInProcessWidgetComponent implements OnInit, OnChanges {
  @Input() account: Account | null = null;
  /** Accounts set to Exclude from Dashboard Analytics that this view puts back in. */
  @Input() includedAccountIds: number[] = [];

  isReady = false;
  count = 0;
  totalOpenValueAmt = 0;
  oldestDate: string | null = null;
  joData: JoInProcessData[] = [];

  private allJoData: JoInProcessData[] = [];
  private allJoHeaders: JoHeader[] = [];

  constructor(
    private dbQueryService: DbQueryService,
    private dialogService: DialogService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const changed = changes['account'] ?? changes['includedAccountIds'];
    if (changed && !changed.firstChange) {
      this.applyAccountFilter();
    }
  }

  private async loadData(): Promise<void> {
    this.isReady = false;

    try {
      // Get full data for grid/export
      const [joHeaders, openValues] = await Promise.all([
        this.dbQueryService.createOpenJoHeadersQuery().execute() as Promise<JoHeader[]>,
        this.dbQueryService.getJoInProcessOpenValues(),
      ]);
      this.allJoHeaders = joHeaders;
      const openValueByJoHeaderId = new Map(openValues.map(v => [v.joHeaderId, v.openValueAmt]));

      // Map to grid-friendly data structure
      this.allJoData = this.allJoHeaders.map(joh => ({
        id: joh.id,
        accountName: joh.account?.accountName || 'N/A',
        joDate: joh.joDate,
        joStatusName: joh.joStatus?.name || 'N/A',
        openValueAmt: openValueByJoHeaderId.get(joh.id) ?? 0,
      }));

      this.applyAccountFilter();
      this.isReady = true;
    } catch (error) {
      console.error('Error loading JO in process count:', error);
      this.count = 0;
      this.totalOpenValueAmt = 0;
      this.oldestDate = null;
      this.joData = [];
      this.allJoData = [];
      this.isReady = true;
    }
  }

  private applyAccountFilter(): void {
    if (this.account) {
      const accountId = this.account.id;
      this.joData = this.allJoData.filter(jod =>
        this.allJoHeaders.some(joh => joh.id === jod.id && joh.accountId === accountId)
      );
    } else {
      const excludedIds = new Set(
        this.allJoHeaders
          .filter(joh => joh.account?.excludeFromAnalytics && !this.includedAccountIds.includes(joh.accountId))
          .map(joh => joh.id),
      );
      this.joData = this.allJoData.filter(jod => !excludedIds.has(jod.id));
    }

    this.count = this.joData.length;
    this.totalOpenValueAmt = _.sumBy(this.joData, jod => jod.openValueAmt);

    // Find oldest JO date
    if (this.joData.length > 0) {
      const oldest = this.joData.reduce((min, jod) =>
        (jod.joDate && min.joDate && jod.joDate < min.joDate) ? jod : min
      );
      if (oldest.joDate) {
        this.oldestDate = DateFns.fmtDate(oldest.joDate);
      } else {
        this.oldestDate = null;
      }
    } else {
      this.oldestDate = null;
    }
  }

  navigateToInProcess(): void {
    this.router.navigate(['/in-process/manage/job-orders']);
  }

  async printWorksheet(): Promise<void> {
    if (!this.account) return;

    // Use already-loaded JoHeaders filtered by account
    const accountHeaders = this.allJoHeaders.filter(joh => joh.accountId === this.account.id);
    if (accountHeaders.length === 0) return;

    const uniqueAddresses = this.extractUniqueShippingAddresses(accountHeaders);

    let selectedAddress: ShippingAddressItem;

    if (uniqueAddresses.length > 1) {
      // Show dialog for user to select one address
      const ref = this.dialogService.open(SelectShippingAddressDialogComponent, {
        header: 'Select Shipping Address',
        width: '600px',
        height: '500px',
        closable: true,
        draggable: true,
        modal: true,
        focusOnShow: false,
        data: { addresses: uniqueAddresses },
      });
      const selected: ShippingAddressItem | null = await firstValueFrom(ref.onClose);
      if (!selected) return; // User cancelled
      selectedAddress = selected;
    } else {
      selectedAddress = uniqueAddresses[0];
    }

    // Build PDF URL with shippingAddress1 as the single filter param
    let routePath = 'printable-open-jo-detail?accountId=' + this.account.id;
    routePath += '&shipAddr1=' + encodeURIComponent(selectedAddress.shippingAddress1);

    const pdfUrl = environment.apiRoot
      + 'api/pdf/pdffromurl/OpenJoDetailReport'
      + '?url=' + encodeURIComponent(UtilFns.getBaseUrl() + routePath)
      + '&headerLeft=' + encodeURIComponent('Open Job Order Detail');
    window.open(pdfUrl, '_blank');
  }

  private extractUniqueShippingAddresses(joHeaders: JoHeader[]): ShippingAddressItem[] {
    const byAddr = _.groupBy(joHeaders, joh => joh.shippingAddress1 || '');

    return Object.entries(byAddr)
      .map(([addr1, headers]) => ({
        shippingAddress1: addr1,
        joCount: headers.length,
      }))
      .sort((a, b) => a.shippingAddress1.localeCompare(b.shippingAddress1));
  }

  // ViewableWidget interface methods
  async exportToExcel(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('JO In Process');
    const exportDate = new Date();

    // Add title row
    worksheet.addRow(['Job Orders In Process']);
    worksheet.getRow(1).font = { bold: true, size: 14 };
    worksheet.mergeCells('A1:E1');

    // Add export date row
    worksheet.addRow([`Exported: ${exportDate.toLocaleDateString()} ${exportDate.toLocaleTimeString()}`]);
    worksheet.getRow(2).font = { italic: true, size: 10 };
    worksheet.mergeCells('A2:E2');

    // Add blank row
    worksheet.addRow([]);

    // Add header row
    worksheet.addRow(['Job Order ID', 'Account', 'JO Date', 'Status', 'Open Value']);
    const headerRow = worksheet.getRow(4);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' },
    };

    // Set column widths
    worksheet.getColumn(1).width = 15;
    worksheet.getColumn(2).width = 30;
    worksheet.getColumn(3).width = 15;
    worksheet.getColumn(4).width = 20;
    worksheet.getColumn(5).width = 15;

    // Add data rows
    this.joData.forEach((item) => {
      worksheet.addRow([
        item.id,
        item.accountName,
        item.joDate,
        item.joStatusName,
        item.openValueAmt,
      ]);
    });

    // Format date and currency columns
    for (let row = 5; row <= worksheet.rowCount; row++) {
      worksheet.getCell(`C${row}`).numFmt = 'mm/dd/yyyy';
      worksheet.getCell(`E${row}`).numFmt = '$#,##0.00';
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `JO_InProcess_${exportDate.toISOString().split('T')[0]}.xlsx`);
  }

  getGridData(): JoInProcessData[] {
    return this.joData;
  }

  getColumnDefs(): ColDef[] {
    return [
      { field: 'id', headerName: 'Job Order ID', flex: 1 },
      { field: 'accountName', headerName: 'Account', flex: 2 },
      { field: 'joDate', headerName: 'JO Date', flex: 1, valueFormatter: (p) => DateFns.fmtDate(p.value) },
      { field: 'joStatusName', headerName: 'Status', flex: 1 },
      { field: 'openValueAmt', headerName: 'Open Value', flex: 1, type: 'rightAligned', cellClass: 'cell-right-justify', valueFormatter: (p) => UtilFns.fmtCurrency(p.value) },
    ];
  }

  getExportFileName(): string {
    return 'JO_InProcess';
  }

  getDialogTitle(): string {
    return 'Job Orders In Process';
  }
}
